import { useEffect, useState } from "react";

import { Mixpanel } from "lib/mixpanel/mixpanel";
import { useUserData } from "lib/store/user";
import { MIXPANEL_TOKEN } from "lib/constants";
import { Filter } from "./components/filter";
import { sortBy } from "./utils";
import Row from "./row/row";
import SortButton from "./sortButton/sortButton";
import SimpleButton from "components/simpleButton/simpleButton";
import { downloadCSV } from "./components/downloadCSV";
import { ClientOnlyPaywall } from "components/paywallModal/clientOnlyPaywall";
import { PaywallWindow } from "lib/types/paywalls";

import type { ConfigType, TableDataType } from "./types";

let mixpanel: Mixpanel | null = null;
/**
 * Arrays are used for the data and the config, as they have stable orders
 *
 * TODO: Mixpanel - Config file should have a Mixpanel event name that is then fed into
 * the mixpanel.track() func
 */
const Table = ({
  tableConfig,
  tableEntries,
  filterType = "location",
  disableDownload = true,
  isPro = false,
  downloadPaywall = null,
}: {
  tableConfig: ConfigType;
  tableEntries: TableDataType[];
  filterType?: string;
  disableDownload?: boolean;
  isPro?: boolean;
  downloadPaywall?: PaywallWindow | null;
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { userId, isProUser } = useUserData();
  const [showDownloadPaywall, setShowDownloadPaywall] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    mixpanel = new Mixpanel(MIXPANEL_TOKEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filter, setFilter] = useState<string>("All");
  const [tableData, setTableData] = useState<TableDataType[]>([
    ...sortBy(tableConfig.initialSort, tableEntries, tableConfig.isAscending),
  ]); // Generate sorting map based on columns
  const SORT_MAP: { [key: string]: string } = {};
  tableConfig.columns.forEach((column) => {
    if (column.sortable && column.dataName) {
      SORT_MAP[column.dataName] = column.displayName || column.dataName;
    }
  });
  // Get sortable columns for the UI, with rank first
  const sortableColumns = tableConfig.columns
    .filter((col) => {
      if (!col.sortable || !col.dataName) return false;
      if (col.displayName === "Company" || col.displayName === "Investor")
        return false;

      return true;
    })
    .map((col) => col.dataName!);

  // Ensure rank is first if it exists
  let sortingOptions = sortableColumns;
  if (sortableColumns.includes("rank")) {
    sortingOptions = [
      "rank",
      ...sortableColumns.filter((col) => col !== "rank"),
    ];
  }

  type SortType = {
    key: string;
    value: "ASC" | "DESC";
  };

  const [sort, setSort] = useState<SortType>({
    key: tableConfig.initialSort,
    value: tableConfig.isAscending ? "ASC" : "DESC",
  });
  const handleSortChange = ({ key }: Pick<SortType, "key">) => {
    if (key === sort.key) {
      if (sort.value === "ASC") {
        const newSort = { key: key, value: "DESC" as const };
        setSort(newSort);
        setTableData([...sortBy(key, tableEntries, false)]);
      }
      if (sort.value === "DESC") {
        const newSort = { key: key, value: "ASC" as const };
        setSort(newSort);
        setTableData([...sortBy(key, tableEntries, true)]);
      }
    } else {
      const newSort = { key: key, value: "DESC" as const };
      setSort(newSort);
      setTableData([...sortBy(key, tableEntries, false)]);
    }

    if (userId && mixpanel) {
      mixpanel.track(`${tableConfig.trackingName} Table Sorting`, {
        "Sorted By": key,
        distinct_id: userId,
      });
    }
  };

  const handleDownloadCSV = () => {
    if (disableDownload) return;

    if (userId && mixpanel) {
      mixpanel.track(`${tableConfig.trackingName} download`, {
        table: document.title,
        distinct_id: userId,
      });
    }

    if (isHydrated && isProUser) {
      downloadCSV(tableData);
      return;
    }

    if (!isProUser && downloadPaywall) {
      setShowDownloadPaywall(true);
    }
  };
  if (tableConfig.columns.length === 0) return null;
  if (!tableEntries || tableEntries.length === 0) return null;
  return (
    <>
      {showDownloadPaywall && downloadPaywall && (
        <ClientOnlyPaywall paywall={downloadPaywall} />
      )}
      <div className={"flex flex-col"}>
        <div className="sticky left-0 pb-2 md:pb-8">
          <Filter
            tableEntries={tableEntries}
            filter={filter}
            filterType={filterType}
            setFilter={setFilter}
            setTableData={setTableData}
            tableConfig={tableConfig}
            mixpanel={mixpanel}
            id={userId}
          />
        </div>

        {sortingOptions.length > 0 && (
          <div className={"flex flex-col"}>
            <div className={"flex flex-col lg:flex-row lg:justify-between"}>
              <ul
                aria-label={"List of sort options"}
                className={"flex gap-[26px] overflow-y-auto"}
              >
                {sortingOptions.map((sortKey) => {
                  return (
                    <li
                      key={sortKey}
                      className={
                        "relative after:absolute after:right-[-12px] after:top-[16px] after:h-[18px] after:w-[2px] after:bg-mono-20 after:content-[''] last:after:content-none"
                      }
                    >
                      <SortButton
                        label={SORT_MAP[sortKey]}
                        columnKey={sortKey}
                        sort={sort.key === sortKey ? sort.value : null}
                        onClick={() => handleSortChange({ key: sortKey })}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={"mt-4 flex flex-col items-start gap-[6px]"}>
              <SimpleButton
                size="md"
                variant="primary"
                accent="pro"
                onClick={handleDownloadCSV}
                disabled={disableDownload}
                data-testid="download-csv"
              >
                Download Data
              </SimpleButton>
              {isHydrated && !isProUser && !disableDownload && (
                <div className={"text-mono-60 text-[12px]"}>
                  Available to Sifted Pro subscribers
                </div>
              )}
            </div>
          </div>
        )}

        <div className={"mb-6 mt-3"}>
          <ul className={"flex flex-col gap-[10px]"}>
            {tableData.map((entry, index) => {
              return (
                <li key={index}>
                  <Row
                    entry={entry}
                    tableConfig={tableConfig}
                    index={index}
                    onTrackMixpanel={async (event, data) => {
                      if (userId && mixpanel) {
                        mixpanel.track(event, { ...data, distinct_id: userId });
                      }
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Table;
